"use client";
import React, { useState } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useDispatch } from "react-redux";
import { useRouter } from "next/navigation";
import { IoEyeSharp, IoEyeOffSharp } from "react-icons/io5";
import { FaCheck } from "react-icons/fa";
import { NotificationService } from "../../../service/NotificationService";
import { AppDispatch } from "../../../feature/store/store";
import { changePasswordApi } from "../../../feature/reducers/userSlice";
import { buttonClass, errorClass, errorText, inputClass, labelClass } from "./style";


// Gleiche Regeln wie beim Registrieren
const PASSWORD_RULES = [
  { label: "Mindestens 8 Zeichen", test: (v: string) => v.length >= 8 },
  { label: "Ein Großbuchstabe", test: (v: string) => /[A-Z]/.test(v) },
  { label: "Ein Kleinbuchstabe", test: (v: string) => /[a-z]/.test(v) },
  { label: "Eine Zahl", test: (v: string) => /\d/.test(v) },
];

interface ChangePasswordFormProps {
  email: string;
}

const ChangePassword: React.FC<ChangePasswordFormProps> = ({ email }) => {
  const dispatch = useDispatch<AppDispatch>();
  const router = useRouter();
  const [showPassword, setShowPassword] = useState(false);

  const formik = useFormik({
    initialValues: { newPassword: "", confirmPassword: "" },
    validationSchema: Yup.object({
      newPassword: Yup.string()
        .matches(/^(?=.*[A-Z])(?=.*[a-z])(?=.*\d).{8,}$/, "Das Passwort erfüllt noch nicht alle Regeln.")
        .required("Neues Passwort ist erforderlich"),
      confirmPassword: Yup.string()
        .oneOf([Yup.ref("newPassword")], "Die Passwörter stimmen nicht überein")
        .required("Bitte Passwort wiederholen"),
    }),
    onSubmit: async (values) => {
      try {
        await dispatch(
          changePasswordApi({
            email,
            password: values.newPassword,
            newPassword: values.newPassword,
            confirmPassword: values.confirmPassword,
          })
        ).unwrap();
        NotificationService.success("Passwort geändert! Sie können sich jetzt anmelden.");
        router.push("/login");
      } catch (error: any) {
        NotificationService.error(errorText(error, "Passwort konnte nicht geändert werden."));
      }
    },
  });

  const passwordError = formik.touched.newPassword && formik.errors.newPassword;
  const confirmError = formik.touched.confirmPassword && formik.errors.confirmPassword;

  return (
    <form onSubmit={formik.handleSubmit} noValidate className="flex flex-col gap-5">
      <div className="flex flex-col gap-2">
        <label htmlFor="newPassword" className={labelClass}>Neues Passwort</label>
        <div className="relative">
          <input
            type={showPassword ? "text" : "password"}
            id="newPassword"
            name="newPassword"
            autoComplete="new-password"
            value={formik.values.newPassword}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            aria-invalid={!!passwordError}
            aria-describedby="password-rules"
            className={`${inputClass} pr-12 ${passwordError ? "border-red-500" : "border-ao-line-strong"}`}
          />
          <button
            type="button"
            aria-label={showPassword ? "Passwort verbergen" : "Passwort anzeigen"}
            onClick={() => setShowPassword((v) => !v)}
            className="absolute inset-y-0 right-0 flex w-12 items-center justify-center text-ao-muted hover:text-ao-fg"
          >
            {showPassword ? <IoEyeOffSharp className="text-lg" /> : <IoEyeSharp className="text-lg" />}
          </button>
        </div>
        <ul id="password-rules" className="grid grid-cols-2 gap-x-4 gap-y-1.5 pt-1">
          {PASSWORD_RULES.map((rule) => {
            const ok = rule.test(formik.values.newPassword);
            return (
              <li key={rule.label} className={`flex items-center gap-2 text-[13px] ${ok ? "text-ao-fg" : "text-ao-muted"}`}>
                <span
                  className={`flex h-4 w-4 shrink-0 items-center justify-center rounded-full text-[9px] ${
                    ok ? "bg-ao-accent text-ao-on-accent" : "border border-ao-line-strong"
                  }`}
                >
                  {ok && <FaCheck />}
                </span>
                {rule.label}
              </li>
            );
          })}
        </ul>
      </div>

      <div className="flex flex-col gap-2">
        <label htmlFor="confirmPassword" className={labelClass}>Passwort wiederholen</label>
        <input
          type={showPassword ? "text" : "password"}
          id="confirmPassword"
          name="confirmPassword"
          autoComplete="new-password"
          value={formik.values.confirmPassword}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          aria-invalid={!!confirmError}
          className={`${inputClass} ${confirmError ? "border-red-500" : "border-ao-line-strong"}`}
        />
        {confirmError && <p className={errorClass}>{formik.errors.confirmPassword}</p>}
      </div>

      <button type="submit" disabled={formik.isSubmitting} className={buttonClass}>
        {formik.isSubmitting ? "Wird gespeichert…" : "Passwort speichern"}
      </button>
    </form>
  );
};

export default ChangePassword;
